import { useEffect, MutableRefObject } from "react";
import { useBoolean } from "@chakra-ui/react";

type TExpandableElements = {
    ref: MutableRefObject<HTMLDivElement | null>;
    maxHeight: number;
};

/**
 * Manage the expandable behavior of a DOM element within a React component.
 * It enables you to toggle the expansion of an element based on its content height and a specified maximum height.
 */

export const useExpandableElements = ({ ref, maxHeight }: TExpandableElements) => {
    const [isExpandable, setIsExpandable] = useBoolean(false);
    const [isExpanded, setIsExpanded] = useBoolean(false);

    useEffect(() => {
        const handleResize = () => {
            if (isExpanded && ref.current && ref.current.style.height !== "auto") {
                ref.current.style.height = "auto";
            }
        };

        if (!isExpanded && ref.current && ref.current.scrollHeight > maxHeight) {
            ref.current.style.height = `${maxHeight}px`;
            setIsExpandable.on();
        }

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [ref, maxHeight, isExpanded, setIsExpandable]);

    const showAll = () => {
        if (ref.current) {
            ref.current.style.height = `${ref.current.scrollHeight}px`;
        }

        setIsExpanded.on();
        setIsExpandable.off();
    };

    return { isExpandable, isExpanded, showAll };
};
